import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Input, Modal, Popconfirm, Select, Space, Table, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { baseURL } from '../API/url';
import {
  CatergoryLinkModal,
  RecordField,
  RecordFormModal,
  compareText,
  errorMessage,
  formatMoney,
  renderCatergory,
} from './managementShared';
import { ManagedAdvisor, advisorName } from './AdvisorManagementModal';

export interface ManagedEMD {
  recId: string;
  firm: string | null;
  contactFirstname: string | null;
  contactLastname: string | null;
  contactEmail: string | null;
  phone: string | null;
  totalAdvisor: number | string | null;
  totalAssets: number | string | null;
  categoryRecId?: string | null;
  categoryDescription?: string | null;
}

interface EMDManagementModalProps {
  open: boolean;
  onClose: () => void;
}

interface LinkResult {
  advisor: ManagedAdvisor;
  emds: ManagedEMD[];
}

const EMD_FIELDS: RecordField[] = [
  { name: 'firm', label: 'Firm', required: true },
  { name: 'contactFirstname', label: 'Contact First Name' },
  { name: 'contactLastname', label: 'Contact Last Name' },
  { name: 'contactEmail', label: 'Contact Email', email: true },
  { name: 'phone', label: 'Phone' },
];

const contactName = (emd: ManagedEMD) =>
  [emd.contactFirstname, emd.contactLastname].filter(Boolean).join(' ');

const EMDManagementModal: React.FC<EMDManagementModalProps> = ({ open, onClose }) => {
  const [emds, setEmds] = useState<ManagedEMD[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [editing, setEditing] = useState<ManagedEMD | 'new' | null>(null);
  const [saving, setSaving] = useState(false);

  const [catergoryTarget, setCatergoryTarget] = useState<ManagedEMD | null>(null);
  const [savingCatergory, setSavingCatergory] = useState(false);

  const [advisorsTarget, setAdvisorsTarget] = useState<ManagedEMD | null>(null);
  const [members, setMembers] = useState<ManagedAdvisor[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [allAdvisors, setAllAdvisors] = useState<ManagedAdvisor[]>([]);
  const [pickedAdvisorId, setPickedAdvisorId] = useState<string | null>(null);
  const [addingAdvisor, setAddingAdvisor] = useState(false);

  const loadEmds = useCallback(async (term: string) => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseURL}/emd/management`, {
        params: term ? { search: term } : {},
      });
      setEmds(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error(errorMessage(error, 'Unable to load EMDs.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSearch('');
      loadEmds('');
    }
  }, [open, loadEmds]);

  const mergeEmds = (updated: ManagedEMD[]) =>
    setEmds((rows) =>
      rows.map((row) => {
        const match = updated.find((u) => u.recId === row.recId);
        return match ? { ...row, ...match, categoryDescription: row.categoryDescription } : row;
      }),
    );

  const replaceRow = (updated: ManagedEMD) =>
    setEmds((rows) => rows.map((row) => (row.recId === updated.recId ? updated : row)));

  const handleSave = async (values: Record<string, any>) => {
    setSaving(true);
    try {
      if (editing === 'new') {
        const response = await axios.post(`${baseURL}/emd/management`, values);
        setEmds((rows) => [response.data, ...rows]);
        message.success('EMD created.');
      } else if (editing) {
        const response = await axios.put(`${baseURL}/emd/management/${editing.recId}`, values);
        replaceRow(response.data);
        message.success('EMD updated.');
      }
      setEditing(null);
    } catch (error) {
      message.error(errorMessage(error, 'Unable to save the EMD.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (emd: ManagedEMD) => {
    try {
      await axios.delete(`${baseURL}/emd/management/${emd.recId}`);
      setEmds((rows) => rows.filter((row) => row.recId !== emd.recId));
      message.success(`${emd.firm || 'EMD'} deleted.`);
    } catch (error) {
      message.error(errorMessage(error, 'Unable to delete the EMD.'));
    }
  };

  const handleSaveCatergory = async (categoryRecId: string | null) => {
    if (!catergoryTarget) return;
    setSavingCatergory(true);
    try {
      const response = await axios.put(
        `${baseURL}/emd/management/${catergoryTarget.recId}/catergory`,
        { categoryRecId },
      );
      replaceRow(response.data);
      message.success(categoryRecId ? 'Catergory linked.' : 'Catergory unlinked.');
      setCatergoryTarget(null);
    } catch (error) {
      message.error(errorMessage(error, 'Unable to link the catergory.'));
    } finally {
      setSavingCatergory(false);
    }
  };

  const loadMembers = async (emd: ManagedEMD) => {
    setLoadingMembers(true);
    try {
      const [membersResponse, advisorsResponse] = await Promise.all([
        axios.get(`${baseURL}/emd/management/${emd.recId}/advisors`),
        axios.get(`${baseURL}/advisor-management`),
      ]);
      setMembers(Array.isArray(membersResponse.data) ? membersResponse.data : []);
      setAllAdvisors(Array.isArray(advisorsResponse.data) ? advisorsResponse.data : []);
    } catch (error) {
      message.error(errorMessage(error, 'Unable to load advisors.'));
    } finally {
      setLoadingMembers(false);
    }
  };

  const openAdvisors = (emd: ManagedEMD) => {
    setAdvisorsTarget(emd);
    setPickedAdvisorId(null);
    setMembers([]);
    loadMembers(emd);
  };

  const applyLinkResult = async (emd: ManagedEMD, result: LinkResult) => {
    mergeEmds(result.emds || []);
    await loadMembers(emd);
  };

  const handleAddAdvisor = async () => {
    if (!advisorsTarget || !pickedAdvisorId) return;
    setAddingAdvisor(true);
    try {
      const response = await axios.post(
        `${baseURL}/emd/management/${advisorsTarget.recId}/advisors/${pickedAdvisorId}`,
      );
      message.success('Advisor added.');
      setPickedAdvisorId(null);
      await applyLinkResult(advisorsTarget, response.data);
    } catch (error) {
      message.error(errorMessage(error, 'Unable to add the advisor.'));
    } finally {
      setAddingAdvisor(false);
    }
  };

  const handleRemoveAdvisor = async (advisor: ManagedAdvisor) => {
    if (!advisorsTarget) return;
    try {
      const response = await axios.delete(
        `${baseURL}/emd/management/${advisorsTarget.recId}/advisors/${advisor.recId}`,
      );
      message.success(`${advisorName(advisor) || 'Advisor'} removed.`);
      await applyLinkResult(advisorsTarget, response.data);
    } catch (error) {
      message.error(errorMessage(error, 'Unable to remove the advisor.'));
    }
  };

  const candidates = allAdvisors.filter((a) => a.emdId !== advisorsTarget?.recId);
  const pickedAdvisor = candidates.find((a) => a.recId === pickedAdvisorId);
  const movingFrom = pickedAdvisor?.emdId ? pickedAdvisor.emdFirm || 'another EMD' : null;

  const columns = [
    {
      title: 'Firm',
      dataIndex: 'firm',
      key: 'firm',
      sorter: (a: ManagedEMD, b: ManagedEMD) => compareText(a.firm, b.firm),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_: unknown, record: ManagedEMD) => contactName(record),
    },
    { title: 'Email', dataIndex: 'contactEmail', key: 'contactEmail' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    {
      title: 'Total Advisors',
      key: 'totalAdvisor',
      render: (_: unknown, record: ManagedEMD) => Number(record.totalAdvisor ?? 0),
    },
    {
      title: 'Total Assets',
      key: 'totalAssets',
      render: (_: unknown, record: ManagedEMD) => formatMoney(record.totalAssets),
    },
    {
      title: 'Catergory',
      key: 'catergory',
      render: (_: unknown, record: ManagedEMD) => renderCatergory(record),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: ManagedEMD) => (
        <Space size="small" wrap>
          <Button size="small" onClick={() => setEditing(record)}>
            Edit
          </Button>
          <Button size="small" onClick={() => openAdvisors(record)}>
            Advisors
          </Button>
          <Button size="small" onClick={() => setCatergoryTarget(record)}>
            Link Catergory
          </Button>
          <Popconfirm
            title="Delete this EMD?"
            description="Its advisors will be unassigned. This cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const memberColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (_: unknown, record: ManagedAdvisor) => advisorName(record),
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: ManagedAdvisor) => (
        <Popconfirm
          title="Remove this advisor from the EMD?"
          okText="Remove"
          okButtonProps={{ danger: true }}
          onConfirm={() => handleRemoveAdvisor(record)}
        >
          <Button size="small" danger>
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="EMD Management"
        open={open}
        onCancel={onClose}
        footer={<Button onClick={onClose}>Close</Button>}
        width={1200}
      >
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Input.Search
            placeholder="Search firm, contact or email"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={(value) => loadEmds(value.trim())}
            style={{ width: 360 }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing('new')}>
            Add EMD
          </Button>
        </Space>
        <Table
          rowKey="recId"
          size="small"
          loading={loading}
          dataSource={emds}
          columns={columns}
          pagination={{ pageSize: 20, showSizeChanger: false }}
        />
      </Modal>

      {editing && (
        <RecordFormModal
          title={editing === 'new' ? 'Add EMD' : 'Edit EMD'}
          fields={EMD_FIELDS}
          initialValues={
            editing === 'new'
              ? {}
              : Object.fromEntries(
                  EMD_FIELDS.map((f) => [f.name, editing[f.name as keyof ManagedEMD] || '']),
                )
          }
          saving={saving}
          onCancel={() => setEditing(null)}
          onSubmit={handleSave}
        />
      )}

      <CatergoryLinkModal
        title={`Link Catergory — ${catergoryTarget?.firm || ''}`}
        open={!!catergoryTarget}
        currentRecId={catergoryTarget?.categoryRecId}
        saving={savingCatergory}
        onCancel={() => setCatergoryTarget(null)}
        onSave={handleSaveCatergory}
      />

      <Modal
        title={`Advisors — ${advisorsTarget?.firm || ''}`}
        open={!!advisorsTarget}
        onCancel={() => setAdvisorsTarget(null)}
        footer={<Button onClick={() => setAdvisorsTarget(null)}>Close</Button>}
        width={900}
        destroyOnHidden
      >
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Select
              aria-label="Advisor to add"
              style={{ width: '100%' }}
              placeholder="Search advisors to add"
              allowClear
              showSearch
              optionFilterProp="label"
              loading={loadingMembers}
              value={pickedAdvisorId ?? undefined}
              onChange={(value) => setPickedAdvisorId(value ?? null)}
              options={candidates.map((a) => ({
                value: a.recId,
                label: [
                  advisorName(a) || '(no name)',
                  a.email ? `(${a.email})` : '',
                  a.emdFirm ? `— currently ${a.emdFirm}` : '',
                ]
                  .filter(Boolean)
                  .join(' '),
              }))}
            />
            <Button
              type="primary"
              disabled={!pickedAdvisorId}
              loading={addingAdvisor}
              onClick={handleAddAdvisor}
            >
              {movingFrom ? 'Move Advisor' : 'Add Advisor'}
            </Button>
          </Space.Compact>
          {movingFrom && (
            <Alert
              type="warning"
              showIcon
              message={`${advisorName(pickedAdvisor!)} will be moved from ${movingFrom}.`}
            />
          )}
        </Space>
        <Typography.Text type="secondary">
          {members.length} advisor{members.length === 1 ? '' : 's'}
        </Typography.Text>
        <Table
          rowKey="recId"
          size="small"
          loading={loadingMembers}
          dataSource={members}
          columns={memberColumns}
          pagination={{ pageSize: 10, showSizeChanger: false }}
        />
      </Modal>
    </>
  );
};

export default EMDManagementModal;
